import { createLandingDocumentSession, projectFromSession, projectToDocument } from '../editor/landingDocumentSession.js';
import { stateful, useEffect, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import { type LandingLocale, landingPageContentRepository } from '../../shared/content/landingContent.js';
import whiteLogo from '../../img/logo/logowhite.png';
import { cursorEffectDefinitions } from '../cursor/cursorEffects.js';
import { LandingEditorClient, type LandingEditorVersionEntry } from '../editor/landingEditorClient.js';
import { landingProjectContentRepository } from '../editor/landingProjectContent.js';
import { LandingEditorElementRegistry, getValueAtPath, setValueAtPath, type LandingEditorElementDefinition } from '../editor/landingEditorRegistry.js';
import { editorNodeIdClass, type LandingEditorRenderOptions } from '../editor/landingEditorRender.js';
import { mergeInlineTextDraftIntoProject, type InlineTextDraft } from '../editor/inlineTextCommit.js';
import { cloneEditorValue, createDefaultLandingEditorProject, getAudienceLandingHomeSections, getOrderedStaticPageBuiltInSections, getOrderedStaticSections, type LandingEditorElementFrame, type LandingEditorProject, type LandingEditorProjectSettings, type LandingHomeSectionKey } from '../../shared/editor/landingEditorTypes.js';
import { footerSection } from '../sections/footerSection.js';
import { renderLandingHomeSection } from '../sections/homeSections.js';
import { landingNavigation } from '../sections/navigation.js';
import { staticPageContent } from './staticPage.js';
import { useEditorPreviewEffects } from './editorPreviewEffects.js';
import { LandingEditorView } from './editorView.js';
import { createEditorProjectOperations } from './editorProjectOperations.js';
import { createRenderMediaPreview } from './renderMediaPreview.js';
import type { StaticPageAudience, StaticPageKey } from '../../shared/content/staticPageContent.js';
import {
  clampZoom,
  colorValue,
  defaultFrame,
  editorIconChoices,
  editorIconLabel,
  editorPages,
  editorPreviewProfiles,
  platformAdminLoginHref,
  formatVersionDate,
  frameInputValue,
  frameKeyWithIndex,
  landingAnchorSection,
  matchingRepeatFrameKey,
  mediaAcceptForKind,
  mediaLabelForKind,
  mutableArrayAtPath,
  normalizeEditableText,
  opacityPercent,
  optionalNumberValue,
  repeatIdTargetForElementId,
  repeatTargetForDefinition,
  replaceFirstNumericSegment,
  sectionTitle,
  statusLabel,
  versionKindLabel,
  visibleEditorSectionKeys,
  type EditorPageKey, type EditorStatus, type InlineTextGeometry,
} from './editorPageConfig.js';
import { useEditorShellState } from './editorShellState.js';
import { revealPreviewSelectionInLayers, rootSectionDefinitionFor, staticPageBuiltInSectionKeyFromDefinition } from './editorSelection.js';
import { appendTestimonial, testimonialDefinitionFor } from './editorTestimonialOperations.js';
import { useEditorGeometryState } from './editorGeometryState.js';
import { createEditorGeometryOperations } from './editorGeometryOperations.js';
import { createEditorAutoLayoutOperations } from './editorAutoLayoutOperations.js';
import { useEditorCommentsState } from './editorCommentsState.js';
import { commitActiveVivaProseMirrorInlineEditor } from '../editor/vivaProseMirrorInlineEditor.js';
import { EditorLocalizationFlowController } from '../../shared/editor/landingEditorLocalizationFlow.js';
import { createEditorLocalizationFlowOperations } from './editorLocalizationFlowOperations.js';
export const LandingEditorPage = stateful(() => {
  const [isAuthenticated, setIsAuthenticated] = useState(LandingEditorClient.isAuthenticated(), []);
  const [email, setEmail] = useState('', []);
  const [password, setPassword] = useState('', []);
  const [locale, setLocaleState] = useState<LandingLocale>(landingPageContentRepository.defaultLocale, []);
  const [project, setProjectState] = useState<LandingEditorProject>(createDefaultLandingEditorProject(), []);
  const documentSession = useMemo(() => [createLandingDocumentSession(project)], []);
  useEffect(() => documentSession.subscribe((_state, transaction) => { if (!transaction || transaction.docChanged) setProjectState(projectFromSession(documentSession)); }), []);
  const setProject = (next: LandingEditorProject) => { documentSession.reset(projectToDocument(next)); setProjectState(projectFromSession(documentSession)); };
  const [activeEditorPage, setActiveEditorPageState] = useState<EditorPageKey>('landing', []);
  const [activeEditorAudience, setActiveEditorAudienceState] = useState<StaticPageAudience>('applicant', []);
  const [selectedElementId, setSelectedElementIdState] = useState('hero.title', []);
  const [status, setStatus] = useState<EditorStatus>('idle', []);
  const [message, setMessage] = useState('', []);
  const [showVersionHistory, setShowVersionHistory] = useState(false, []);
  const [versionHistory, setVersionHistory] = useState<LandingEditorVersionEntry[]>([], []);
  const [isLoadingVersionHistory, setIsLoadingVersionHistory] = useState(false, []);
  const shellState = useEditorShellState();
  const { leftTab, setLeftTab, leftPanelOpen, setLeftPanelOpen, propertiesTab, setPropertiesTab, rightPanelOpen, setRightPanelOpen, activeTool, setActiveTool, activeFrame, setActiveFrame, activePreviewProfile, setActivePreviewProfile, zoom, setZoom, beginCommentPanel, restoreCommentPanel } = shellState;
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({}, []);
  const [draggedSection, setDraggedSection] = useState<LandingHomeSectionKey | null>(null, []);
  const history = documentSession.canUndo ? [project] : [];
  const future = documentSession.canRedo ? [project] : [];
  const [showSettings, setShowSettings] = useState(false, []);
  const [floatingToolbarPosition, setFloatingToolbarPosition] = useState<{ left: number; top: number } | null>(null, []);
  const [sectionToolbarPosition, setSectionToolbarPosition] = useState<{ left: number; top: number } | null>(null, []);
  const [testimonialToolbarPosition, setTestimonialToolbarPosition] = useState<{ left: number; top: number } | null>(null, []);
  const [inlineTextDraft, setInlineTextDraft] = useState<InlineTextDraft | null>(null, []);
  const [inlineTextGeometry, setInlineTextGeometry] = useState<InlineTextGeometry | null>(null, []);
  const [localizationFlow] = useState(new EditorLocalizationFlowController(), []);
  const [localizationFlowRevision, setLocalizationFlowRevision] = useState(0, []);
  void localizationFlowRevision;
  localizationFlow.connect(setLocalizationFlowRevision);
  const geometryState = useEditorGeometryState();
  const [flippedFanSections, setFlippedFanSections] = useState<ReadonlySet<string>>(new Set<string>(), []);
  const handleAuthorizationError = (error: unknown) => {
    if (!LandingEditorClient.isAuthorizationError(error)) return false;
    setIsAuthenticated(false);
    setPassword('');
    setStatus('idle');
    setMessage('Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.');
    return true;
  };
  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    setStatus('loading');
    void LandingEditorClient.loadDraft()
      .then(draft => {
        if (!active) return;
        setProject(draft);
        setStatus('saved');
      })
      .catch(error => {
        if (!active) return;
        if (handleAuthorizationError(error)) return;
        setMessage(error instanceof Error ? error.message : 'Projekt konnte nicht geladen werden.');
        setStatus('error');
      });
    return () => {
      active = false;
    };
  }, [isAuthenticated]);
  const content = landingProjectContentRepository.landing(project, locale);
  const activeStaticPageKey = activeEditorPage === 'landing' ? null : (activeEditorPage as StaticPageKey);
  const orderedSections =
    activeEditorPage === 'landing'
      ? getAudienceLandingHomeSections(project, activeEditorAudience)
      : getOrderedStaticSections(project, activeEditorPage, activeEditorAudience);
  const orderedStaticPageBuiltInSections =
    activeEditorPage === 'landing'
      ? []
      : getOrderedStaticPageBuiltInSections(project, activeEditorPage, activeEditorAudience);
  const definitions = activeEditorPage === 'landing'
    ? LandingEditorElementRegistry.definitions(content)
    : LandingEditorElementRegistry.staticPageDefinitions(
        activeEditorPage as StaticPageKey,
        landingProjectContentRepository.staticPage(
          project,
          locale,
          activeEditorAudience,
          activeEditorPage as StaticPageKey,
        ),
        content,
        orderedSections
      );
  const definitionById = definitions.reduce<Record<string, LandingEditorElementDefinition>>((result, definition) => {
    result[definition.id] = definition;
    return result;
  }, {});
  documentSession.context.locale = locale;
  documentSession.commentNodes.clear();
  const anchorNode = (path: string[], elementId: string) => {
    const key = JSON.stringify(path);
    documentSession.commentNodes.set(key, [...(documentSession.commentNodes.get(key) ?? []), project.editorAnchorIds[elementId] ?? elementId]);
  };
  anchorNode(activeEditorPage === 'landing' ? ['content', locale] : ['staticPages', locale, activeEditorAudience, activeEditorPage], 'page.root');
  for (const definition of definitions) {
    if (!definition.path) continue;
    const path = definition.source === 'static' ? ['staticPages', locale, activeEditorAudience, definition.staticPageKey ?? activeEditorPage] : ['content', locale];
    anchorNode([...path, ...definition.path.split('.')], definition.id);
  }
  const isCommentMode = activeTool === 'comment';
  const commentState = useEditorCommentsState({
    documentSession,
    isAuthenticated,
    enabled: isCommentMode,
    pageKey: activeEditorPage,
    audience: activeEditorAudience,
    locale,
    elementIds: definitions.map(definition => definition.id), anchorIds: project.editorAnchorIds,
    onActivateProfile: profileId => {
      const profile = editorPreviewProfiles.find(option => option.id === profileId);
      if (!profile) return;
      setActivePreviewProfile(profile.id);
      setActiveFrame(profile.editorFrame);
    },
    onAuthorizationError: handleAuthorizationError,
    openRightPanel: () => setRightPanelOpen(true),
  });
  const selectedDefinition = definitions.find(definition => definition.id === selectedElementId) ?? definitions[0];
  const selectedFieldDefinition = selectedDefinition?.path
    ? (selectedDefinition as LandingEditorElementDefinition & { path: string })
    : null;
  const fieldSourceForProject = (
    editorProject: LandingEditorProject,
    definition: LandingEditorElementDefinition,
    contentLocale = locale,
    contentAudience = activeEditorAudience,
  ): Record<string, unknown> => {
    const staticPageKey = definition.source === 'static' ? definition.staticPageKey ?? activeStaticPageKey : null;
    if (staticPageKey) {
      return landingProjectContentRepository.staticPageRecord(
        editorProject,
        contentLocale,
        contentAudience,
        staticPageKey,
      );
    }
    return editorProject.content[contentLocale] as unknown as Record<string, unknown>;
  };
  const fieldValueForDefinition = (definition: LandingEditorElementDefinition & { path: string }) =>
    getValueAtPath(fieldSourceForProject(project, definition), definition.path);
  const selectedFieldValue = selectedFieldDefinition ? fieldValueForDefinition(selectedFieldDefinition) : '';
  const isSelectedText = selectedFieldDefinition?.kind === 'text';
  const isSelectedMedia = selectedFieldDefinition?.kind === 'image' || selectedFieldDefinition?.kind === 'video' || selectedFieldDefinition?.kind === 'track';
  const isSelectedIcon = selectedFieldDefinition?.kind === 'icon';
  const selectedFrame = project.elementFrames[selectedElementId] ?? defaultFrame();
  const orderedSectionIds = new Set(orderedSections.map(section => `section.${section}`));
  const elementKinds = definitions.reduce<Record<string, LandingEditorElementDefinition['kind']>>((result, definition) => {
    result[definition.id] = definition.kind;
    return result;
  }, {});
  const elementIdByClass = definitions.reduce<Record<string, string>>((result, definition) => {
    result[editorNodeIdClass(definition.id)] = definition.id;
    return result;
  }, {});
  const childrenByParent = definitions.reduce<Record<string, LandingEditorElementDefinition[]>>((result, definition) => {
    if (!definition.parentId) return result;
    if (definition.parentId === 'page.root' && definition.kind === 'section' && !orderedSectionIds.has(definition.id)) return result;
    result[definition.parentId] = [...(result[definition.parentId] ?? []), definition];
    return result;
  }, {});
  childrenByParent['page.root'] = [
    ...orderedSections
      .map(section => definitions.find(definition => definition.id === `section.${section}`))
      .filter((definition): definition is LandingEditorElementDefinition => Boolean(definition)),
    ...definitions.filter(definition => definition.parentId === 'page.root' && definition.kind !== 'section'),
  ];
  const mediaDefinitions = definitions.filter(
    definition => definition.kind === 'image' || definition.kind === 'video' || definition.kind === 'track',
  );
  const selectedRootSectionDefinition = rootSectionDefinitionFor(selectedDefinition, definitionById);
  const selectedSection = selectedRootSectionDefinition?.section;
  const selectedTestimonialDefinition = testimonialDefinitionFor(selectedDefinition, definitionById);
  const selectedTestimonialIndex = selectedTestimonialDefinition
    ? Number(selectedTestimonialDefinition.id.split('.')[2])
    : -1;
  const selectedStaticPageBuiltInSection = staticPageBuiltInSectionKeyFromDefinition(
    activeStaticPageKey,
    selectedRootSectionDefinition,
  );
  const activeEditorPageDefinition =
    editorPages.find(page => page.key === activeEditorPage) ??
    ({ key: 'landing', label: 'Landingpage', path: '/' } as const);
  const updateProject = (
    nextProject: LandingEditorProject,
    nextStatus: EditorStatus = 'unsaved',
    options: { trackHistory?: boolean; mergeInlineDraft?: boolean } = {},
  ) => {
    const activeInlineEditor = typeof document === 'undefined' ? null : document.querySelector<HTMLElement>('.vp-editor-inline-textarea');
    if (options.mergeInlineDraft !== false && activeInlineEditor && inlineTextDraft) mergeInlineTextDraftIntoProject(nextProject, { elementId: inlineTextDraft.elementId, value: activeInlineEditor.dataset.value ?? inlineTextDraft.value }, definitions, locale, fieldSourceForProject, normalizeEditableText);
    documentSession.replace(projectToDocument({ ...nextProject, updatedAt: new Date().toISOString() }), { history: options.trackHistory !== false });
    setProjectState(projectFromSession(documentSession));
    setStatus(nextStatus);
  };
  const addTestimonial = () => {
    if (localizationFlow.round) return;
    const { nextProject, insertionIndex } = appendTestimonial(project, locale);
    updateProject(nextProject);
    const repeatRootId = `story.testimonial.${insertionIndex}`;
    setSelectedElementId(repeatRootId);
    markNewRepeatForLocalization(selectedDefinition, repeatRootId, nextProject);
  };
  const updateSettings = (update: Partial<LandingEditorProjectSettings>) => {
    const nextProject = cloneEditorValue(project);
    nextProject.settings = { ...(nextProject.settings ?? {}), ...update };
    updateProject(nextProject);
  };
  const updateField = (
    definition: LandingEditorElementDefinition,
    value: string,
    options: { trackHistory?: boolean; localization?: boolean } = {},
  ) => {
    if (!definition.path) return;
    const previousValue = getValueAtPath(fieldSourceForProject(project, definition), definition.path);
    if (options.localization !== false && !localizationFlow.round && value !== previousValue) {
      localizationFlow.record(definition, definitionById, editorContext({ elementId: definition.id }), previousValue, value);
    }
    const nextProject = cloneEditorValue(project);
    setValueAtPath(fieldSourceForProject(nextProject, definition), definition.path, value);
    updateProject(nextProject, 'unsaved', options);
  };
  const updateFrame = (elementId: string, frameUpdate: Partial<LandingEditorElementFrame>) => {
    const nextProject = cloneEditorValue(project);
    const frame = nextProject.elementFrames[elementId] ?? defaultFrame();
    nextProject.elementFrames[elementId] = { ...frame, ...frameUpdate };
    updateProject(nextProject);
  };
  const geometryOperations = createEditorGeometryOperations({ project, definitions, activeFrame, activePreviewProfile, cloneProject: cloneEditorValue, updateProject });
  const autoLayoutOperations = createEditorAutoLayoutOperations({ project, definitions, activeFrame, cloneProject: cloneEditorValue, updateProject });
  const renderMediaPreview = createRenderMediaPreview({ fieldValueForDefinition, updateField });
  const resetFrame = (elementId: string) => {
    const nextProject = cloneEditorValue(project);
    delete nextProject.elementFrames[elementId];
    updateProject(nextProject);
  };
  const updateFrameHref = (elementId: string, href: string) => {
    const trimmed = href.trim();
    const nextProject = cloneEditorValue(project);
    const frame = nextProject.elementFrames[elementId] ?? defaultFrame();
    if (trimmed) frame.href = trimmed;
    else delete frame.href;
    nextProject.elementFrames[elementId] = frame;
    updateProject(nextProject);
  };
  const localizationOperations = createEditorLocalizationFlowOperations({
    activeEditorAudience, activeEditorPage, cloneEditorValue, defaultFrame, definitions, definitionById,
    expandedSections, fieldSourceForProject, geometryOverlayRect: geometryState.overlayRect, getOrderedStaticSections, handleAuthorizationError,
    inlineTextDraft, landingAnchorSection, landingPageContentRepository, landingProjectContentRepository,
    locale, localizationFlow, normalizeEditableText, project, selectedElementId, setActiveEditorAudienceState,
    setActiveEditorPageState, setExpandedSections, setInlineTextDraft, setInlineTextGeometry,
    setLocaleState, setMessage, setSelectedElementIdState, setStatus, updateProject,
  });
  const {
    editorContext, handleEditorAudienceChange, handleEditorNavigate, handlePreviewLinkClick,
    markNewRepeatForLocalization, markNewSectionForLocalization, recordLocalizedFieldCommit,
    renderLocalizationFlow, setLocale, setSelectedElementId, stageLocalizationTextResult, switchEditorPage,
  } = localizationOperations;
  const {
    addSection: addSectionOperation, applyInlineTextStyle,
    canDeleteDefinition,
    canDuplicateDefinition,
    changeZoom,
    commitInlineTextEditor,
    commitProseMirrorTextEditor,
    deleteDefinition,
    deleteSection,
    duplicateDefinition,
    duplicateSection,
    handleElementPointerDown,
    handleInlineTextKeyDown,
    handlePreviewKeyDown,
    handlePreviewSectionDrop,
    handlePreviewTextCommit,
    handleRedo,
    handleSectionDragOver,
    handleSectionDragStart,
    handleSectionDrop,
    handleUndo,
    moveSection,
    moveRepeatDefinition,
    moveStaticPageBuiltInSection,
    toggleSection,
  } = createEditorProjectOperations({
    activeEditorAudience,
    activeEditorPage,
    clampZoom,
    cloneEditorValue,
    defaultFrame,
    definitions,
    draggedSection,
    elementIdByClass,
    expandedSections,
    fieldSourceForProject,
    fieldValueForDefinition,
    frameKeyWithIndex,
    future,
    getOrderedStaticPageBuiltInSections,
    history,
    inlineTextDraft,
    landingPageContentRepository,
    locale,
    matchingRepeatFrameKey,
    mutableArrayAtPath,
    normalizeEditableText,
    onRepeatAdded: markNewRepeatForLocalization,
    onSectionAdded: markNewSectionForLocalization,
    project,
    recordLocalizedFieldCommit,
    repeatIdTargetForElementId,
    repeatTargetForDefinition,
    replaceFirstNumericSegment,
    selectedFieldDefinition,
    setDraggedSection,
    setExpandedSections,
    undoDocument: () => documentSession.undo(),
    redoDocument: () => documentSession.redo(),
    setInlineTextDraft,
    setInlineTextGeometry,
    setMessage,
    setProject,
    setSelectedElementId,
    setStatus,
    setValueAtPath,
    setZoom,
    stageLocalizationTextResult,
    updateField,
    updateProject,
    zoom,
  });
  const addSection = (section: LandingHomeSectionKey) => {
    if (localizationFlow.round) return;
    addSectionOperation(section);
  };
  const toggleCommentMode = () => {
    if (localizationFlow.round) return;
    if (isCommentMode) { restoreCommentPanel(); return setActiveTool('select'); }
    if (!commitActiveVivaProseMirrorInlineEditor()) commitInlineTextEditor();
    geometryState.lockAll();
    setInlineTextDraft(null); setInlineTextGeometry(null); setShowSettings(false); setShowVersionHistory(false);
    beginCommentPanel(); setActiveTool('comment');
  };
  useEditorPreviewEffects({
    isAuthenticated,
    locale,
    projectUpdatedAt: project.updatedAt, project,
    selectedElementId,
    geometryTargetElementId: geometryState.imageEditingMode === 'mask' ? selectedDefinition?.maskHostId : undefined,
    geometryTargetScale: geometryState.imageEditingMode === 'mask' && selectedDefinition?.kind === 'image' && !selectedDefinition.maskHostId
      ? geometryOperations.imageScaleFor(selectedElementId) / 100
      : undefined,
    leftTab,
    expandedSections,
    activeFrame,
    selectedFieldKind: selectedFieldDefinition?.kind,
    inlineTextDraft,
    inlineTextGeometry,
    zoom,
    isSelectedText,
    isSelectedIcon,
    selectedRootSectionId: selectedRootSectionDefinition?.id,
    hasSelectedSection:
      Boolean(selectedSection || selectedStaticPageBuiltInSection) && !selectedTestimonialDefinition,
    selectedTestimonialId: selectedTestimonialDefinition?.id,
    setInlineTextDraft,
    setInlineTextGeometry,
    setFloatingToolbarPosition,
    setSectionToolbarPosition,
    setTestimonialToolbarPosition,
    setGeometryOverlayRect: geometryState.setOverlayRect,
    commitInlineTextEditor,
    enableSelectedAutoLayout: !isCommentMode && selectedDefinition?.id !== 'page.root' && (selectedDefinition?.kind === 'container' || selectedDefinition?.kind === 'section') ? () => autoLayoutOperations.enableContainer(selectedElementId) : undefined,
  });
  const editorRenderOptions: LandingEditorRenderOptions = {
    project,
    mode: 'editor',
    selectedElementId: isCommentMode ? undefined : localizationFlow.round ? undefined : selectedElementId,
    inlineEditingElementId:
      inlineTextDraft?.elementId === inlineTextGeometry?.elementId ? inlineTextGeometry?.elementId : undefined,
    elementKinds,
    onElementSelect: isCommentMode || Boolean(localizationFlow.round) ? undefined : elementId => revealPreviewSelectionInLayers(elementId, definitionById, expandedSections, setExpandedSections, setLeftTab, setSelectedElementId),
    onElementPointerDown: activeTool === 'select' && !localizationFlow.round ? handleElementPointerDown : undefined,
    onAddTestimonial: isCommentMode ? undefined : localizationFlow.round ? undefined : addTestimonial,
    onNavigate: isCommentMode || Boolean(localizationFlow.round) ? undefined : handleEditorNavigate,
    currentPath: activeEditorPageDefinition.path,
    flippedFanSections,
  };
  const isFanSection = (section: LandingHomeSectionKey | undefined): section is 'heroFanApplicant' | 'heroFanCompany' =>
    section === 'heroFanApplicant' || section === 'heroFanCompany';
  const toggleFanFlip = (section: 'heroFanApplicant' | 'heroFanCompany') => {
    const next = new Set(flippedFanSections);
    if (next.has(section)) next.delete(section);
    else next.add(section);
    setFlippedFanSections(next);
    // Editing happens on the now-visible face; drop any stale inline editor from the other side.
    setInlineTextDraft(null);
    setInlineTextGeometry(null);
  };
  const handleLogin = async (event: SubmitEvent) => {
    event.preventDefault();
    if (!email || !password) {
      setStatus('error');
      setMessage('E-Mail und Passwort werden benötigt.');
      return;
    }
    setStatus('loading');
    setMessage('');
    try {
      const target = await LandingEditorClient.login(email, password);
      setMessage(target === 'backend' ? 'Server-Login aktiv.' : 'Server nicht erreichbar, Offline-Modus aktiv.');
      setIsAuthenticated(true);
      setStatus(target === 'backend' ? 'saved' : 'local');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Login fehlgeschlagen.');
      setStatus('error');
    }
  };
  const refreshVersionHistory = async () => {
    setIsLoadingVersionHistory(true);
    try {
      setVersionHistory(await LandingEditorClient.loadVersionHistory());
    } catch (error) {
      if (handleAuthorizationError(error)) return;
      setMessage(error instanceof Error ? error.message : 'Versionsverlauf konnte nicht geladen werden.');
    } finally {
      setIsLoadingVersionHistory(false);
    }
  };
  const toggleVersionHistory = () => {
    const next = !showVersionHistory;
    setShowVersionHistory(next);
    if (next) void refreshVersionHistory();
  };
  const restoreVersion = (entry: LandingEditorVersionEntry) => {
    if (localizationFlow.round) return;
    updateProject({ ...cloneEditorValue(entry.project), updatedAt: new Date().toISOString() });
    setStatus('unsaved');
    setShowVersionHistory(false);
    setMessage(`Version vom ${formatVersionDate(entry.createdAt)} geladen. Zum Übernehmen bitte speichern oder publishen.`);
  };
  const handleSyncFromPublished = async () => {
    if (localizationFlow.round) return;
    if (
      status === 'unsaved' &&
      typeof window !== 'undefined' &&
      !window.confirm('Ungespeicherte Änderungen werden durch den aktuellen Live-Stand ersetzt. Fortfahren?')
    ) {
      return;
    }
    setStatus('loading');
    setMessage('');
    try {
      const syncedProject = await LandingEditorClient.syncDraftFromPublished();
      setProject(syncedProject);
      setStatus('saved');
      setMessage('Live-Stand aus der Datenbank in den Editor übernommen.');
      if (showVersionHistory) void refreshVersionHistory();
    } catch (error) {
      if (handleAuthorizationError(error)) return;
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Live-Stand konnte nicht übernommen werden.');
    }
  };

  const handleSave = async () => {
    if (localizationFlow.round) return;
    setStatus('saving');
    setMessage('');
    try {
      const target = await LandingEditorClient.saveDraft(project);
      setStatus(target === 'backend' ? 'saved' : 'local');
      setMessage(target === 'backend' ? 'Draft im aktuellen Backend gespeichert.' : 'Draft lokal gespeichert.');
      if (showVersionHistory) void refreshVersionHistory();
    } catch (error) {
      if (handleAuthorizationError(error)) return;
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Speichern fehlgeschlagen.');
    }
  };

  const handlePublish = async () => {
    if (localizationFlow.round) return;
    setStatus('saving');
    setMessage('');
    try {
      const target = await LandingEditorClient.publish(project);
      setStatus(target === 'backend' ? 'published' : 'local');
      setMessage(target === 'backend' ? 'Landingpage ist live veröffentlicht.' : 'Lokal veröffentlicht. Sichtbar in diesem Browser.');
      if (showVersionHistory) void refreshVersionHistory();
    } catch (error) {
      if (handleAuthorizationError(error)) return;
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Publish fehlgeschlagen.');
    }
  };

  const handleMediaUpload = async (definition: LandingEditorElementDefinition & { path: string }, event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const mediaLabel = mediaLabelForKind(definition.kind);

    setStatus('saving');
    setMessage('');
    try {
      const upload = await LandingEditorClient.uploadAsset(file);
      updateField(definition, upload.url);
      setMessage(
        upload.target === 'backend'
          ? `${mediaLabel} ins aktuelle Backend hochgeladen.`
          : `${mediaLabel} lokal als Data-URL gespeichert.`,
      );
    } catch (error) {
      if (handleAuthorizationError(error)) return;
      setStatus('error');
      setMessage(error instanceof Error ? error.message : `${mediaLabel} konnte nicht hochgeladen werden.`);
    } finally {
      input.value = '';
    }
  };

  const handleFaviconUpload = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setStatus('saving');
    setMessage('');
    try {
      const upload = await LandingEditorClient.uploadAsset(file);
      updateSettings({ faviconUrl: upload.url });
    } catch (error) {
      if (handleAuthorizationError(error)) return;
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Favicon konnte nicht hochgeladen werden.');
    } finally {
      input.value = '';
    }
  };

  const handleCursorUpload = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setStatus('saving');
    setMessage('');
    try {
      const upload = await LandingEditorClient.uploadAsset(file);
      updateSettings({ cursorCss: `url('${upload.url}') 0 0, auto` });
    } catch (error) {
      if (handleAuthorizationError(error)) return;
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Cursor konnte nicht hochgeladen werden.');
    } finally {
      input.value = '';
    }
  };

  const handleLogout = () => {
    LandingEditorClient.logout();
    setIsAuthenticated(false);
    setPassword('');
  };

  return new LandingEditorView({
    activeEditorAudience,
    activeEditorPage,
    activeFrame,
    activePreviewProfile,
    activeTool,
    addSection, applyInlineTextStyle,
    canDeleteDefinition,
    canDuplicateDefinition,
    changeZoom,
    childrenByParent,
    commentState,
    definitions,
    colorValue,
    commitInlineTextEditor,
    commitProseMirrorTextEditor,
    content,
    cursorEffectDefinitions,
    deleteDefinition,
    deleteSection,
    duplicateDefinition,
    duplicateSection,
    editorIconChoices,
    editorIconLabel,
    editorPages,
    editorPreviewProfiles,
    editorRenderOptions,
    email,
    platformAdminLoginHref,
    expandedSections,
    fieldValueForDefinition,
    flippedFanSections,
    floatingToolbarPosition,
    footerSection,
    formatVersionDate,
    frameInputValue,
    future,
    geometryState,
    geometryOperations,
    autoLayoutOperations,
    handleCursorUpload,
    handleEditorAudienceChange,
    handleFaviconUpload,
    handleInlineTextKeyDown,
    handleLogin,
    handleLogout,
    handleMediaUpload,
    handlePreviewKeyDown,
    handlePreviewLinkClick,
    handlePreviewSectionDrop,
    handlePreviewTextCommit,
    handlePublish,
    handleRedo,
    handleSave,
    handleSectionDragOver,
    handleSectionDragStart,
    handleSectionDrop,
    handleSyncFromPublished,
    handleUndo,
    history,
    html,
    inlineTextDraft,
    inlineTextGeometry,
    isAuthenticated,
    isCommentMode,
    isFanSection,
    isLoadingVersionHistory,
    isSelectedIcon,
    isSelectedMedia,
    isSelectedText,
    landingAnchorSection,
    landingNavigation,
    landingPageContentRepository,
    landingProjectContentRepository,
    leftTab,
    leftPanelOpen,
    locale,
    localizationFlowActive: Boolean(localizationFlow.round),
    mediaAcceptForKind,
    mediaDefinitions,
    mediaLabelForKind,
    message: message || (project.retainedFields?.length ? `${project.retainedFields.length} unbekannte Datenfelder sind gesichert und werden unverändert mitgeführt.` : ''),
    moveSection, moveRepeatDefinition,
    moveStaticPageBuiltInSection,
    opacityPercent,
    optionalNumberValue,
    orderedSections,
    orderedStaticPageBuiltInSections,
    password,
    project,
    propertiesTab,
    rightPanelOpen,
    refreshVersionHistory,
    renderLandingHomeSection,
    renderLocalizationFlow,
    renderMediaPreview,
    resetFrame,
    restoreVersion,
    sectionTitle,
    sectionToolbarPosition,
    selectedDefinition,
    selectedElementId,
    selectedFieldDefinition,
    selectedFieldValue,
    selectedFrame,
    selectedRootSectionDefinition,
    selectedSection,
    selectedStaticPageBuiltInSection,
    selectedTestimonialDefinition,
    selectedTestimonialIndex,
    setActiveEditorAudience: handleEditorAudienceChange,
    setActiveFrame,
    setActivePreviewProfile,
    setActiveTool: (tool: typeof activeTool) => { if (localizationFlow.round) return; if (isCommentMode) restoreCommentPanel(); setActiveTool(tool); },
    setDraggedSection,
    setEmail,
    setInlineTextDraft,
    setInlineTextGeometry,
    setLeftTab,
    setLeftPanelOpen,
    setLocale,
    setPassword,
    setPropertiesTab,
    setRightPanelOpen,
    setSelectedElementId,
    setShowSettings,
    setShowVersionHistory,
    showSettings,
    showVersionHistory,
    staticPageContent,
    status,
    statusLabel,
    switchEditorPage,
    toggleFanFlip,
    toggleSection,
    toggleCommentMode,
    toggleVersionHistory,
    testimonialCount: content.story.testimonials.length,
    testimonialToolbarPosition,
    updateProject,
    updateField,
    updateFrame,
    updateFrameHref,
    updateSettings,
    versionHistory,
    versionKindLabel,
    visibleEditorSectionKeys,
    whiteLogo,
    zoom,
  }).render();
});
